import { Box, IconButton, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MaterialSymbol } from '@/components/MaterialSymbol';

export type TimelineZoomControlProps = {
  /** Slider position, 0 (a year visible) … 1 (a week visible). */
  sliderValue: number;
  onSliderChange: (t: number) => void;
  onZoomBy: (direction: 1 | -1) => void;
};

/**
 * The timeline's zoom control: minus · slider · plus. Lives at the right edge
 * of the sticky month band, opaque so clipped month labels tuck underneath it.
 * The slider position is geometric (see `timelineZoom.ts`), so equal travel
 * means equal zoom factor.
 */
export const TimelineZoomControl = ({
  sliderValue,
  onSliderChange,
  onZoomBy,
}: TimelineZoomControlProps) => {
  const { t } = useTranslation('pmPlanning');
  return (
    <Box
      data-automation-id="timeline-zoom"
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        // The slider thumb overhangs the rail by half its width at either
        // extreme; less than a 1-unit gap lets it sit on top of the ± buttons
        // and swallow their clicks.
        gap: 1,
        px: 1,
        bgcolor: 'var(--bg-panel)',
        borderLeft: '1px solid var(--border-divider)',
      }}
    >
      <IconButton
        size="tiny"
        title={t('timeline.ZoomOut')}
        aria-label={t('timeline.ZoomOut')}
        data-automation-id="timeline-zoom-out"
        onClick={() => {
          onZoomBy(-1);
        }}
      >
        <MaterialSymbol name="zoom_out" size={15} />
      </IconButton>
      <Slider
        value={sliderValue}
        min={0}
        max={1}
        step={0.01}
        size="small"
        aria-label={t('timeline.Zoom')}
        data-automation-id="timeline-zoom-slider"
        onChange={(_event, value) => {
          if (typeof value === 'number') onSliderChange(value);
        }}
        sx={{ width: 96 }}
      />
      <IconButton
        size="tiny"
        title={t('timeline.ZoomIn')}
        aria-label={t('timeline.ZoomIn')}
        data-automation-id="timeline-zoom-in"
        onClick={() => {
          onZoomBy(1);
        }}
      >
        <MaterialSymbol name="zoom_in" size={15} />
      </IconButton>
    </Box>
  );
};
